import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

interface Dealer {
  id: string;
  name: string;
  slug: string;
  autoPublishEnabled: boolean;
}

export function IntegrationsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    apiGet<{ dealers: Dealer[] }>("/api/dealers")
      .then((data) => {
        setDealers(data.dealers);
        if (data.dealers.length > 0) {
          setSelectedDealerId(user?.dealerId ?? data.dealers[0].id);
        }
      })
      .catch((err) => console.error("dealers load failed:", err))
      .finally(() => setLoading(false));
  }, [user?.dealerId]);

  const gbpStatus = searchParams.get("gbp");
  const fbStatus = searchParams.get("fb");
  const fixture = searchParams.get("fixture") === "1";

  const oauthBase = (provider: "gbp" | "fb") =>
    selectedDealerId
      ? `/oauth/${provider}/start?dealerId=${encodeURIComponent(selectedDealerId)}`
      : "#";

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-8 sm:py-10">
      <h1 className="text-2xl font-semibold text-slate-900">Integrations</h1>
      <p className="mt-1 text-sm text-slate-500">
        Connect a dealer's Google Business Profile and Facebook Pages so we can pull reviews and publish posts.
      </p>

      {(gbpStatus || fbStatus) && (
        <div
          className={`mt-6 rounded-xl border px-4 py-3 text-sm ${
            gbpStatus === "connected" || fbStatus === "connected"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
        >
          {gbpStatus === "connected" && (
            <div>
              Google Business Profile connected{fixture ? " (fixture mode)" : ""}.
            </div>
          )}
          {fbStatus === "connected" && (
            <div>Facebook Pages connected{fixture ? " (fixture mode)" : ""}.</div>
          )}
          {gbpStatus === "error" && (
            <div>GBP connection failed: {searchParams.get("reason") ?? "unknown"}.</div>
          )}
          {fbStatus === "error" && (
            <div>FB connection failed: {searchParams.get("reason") ?? "unknown"}.</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-sm text-slate-500">Loading dealers…</div>
      ) : dealers.length === 0 ? (
        <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          No dealers visible to your account.
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {dealers.length > 1 && (
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Dealer</span>
              <select
                value={selectedDealerId ?? ""}
                onChange={(e) => setSelectedDealerId(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                {dealers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>
          )}

          <IntegrationCard
            title="Google Business Profile"
            description="Read reviews, post replies. Requires Google approval for the v4 API."
            startHref={oauthBase("gbp")}
            disabled={!selectedDealerId}
          />
          <IntegrationCard
            title="Facebook Pages"
            description="Publish scheduled posts via Graph API (pages_manage_posts)."
            startHref={oauthBase("fb")}
            disabled={!selectedDealerId}
          />
        </div>
      )}

      <div className="mt-10 text-xs text-slate-400">
        Routes are wired at <code>{location.pathname}</code> from <code>server/src/services/{`{gbp,facebook}`}/oauth.ts</code>.
      </div>
    </div>
  );
}

function IntegrationCard({
  title,
  description,
  startHref,
  disabled,
}: {
  title: string;
  description: string;
  startHref: string;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <a
          href={disabled ? undefined : startHref}
          aria-disabled={disabled}
          className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium ${
            disabled
              ? "cursor-not-allowed bg-slate-100 text-slate-400"
              : "bg-brand-600 text-white hover:bg-brand-700"
          }`}
        >
          Connect
        </a>
      </div>
    </div>
  );
}
